import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as d3 from 'd3';
import {
  AlertTriangle,
  Play,
  Loader2,
  Download,
  TrendingUp,
  Shield,
  AlertCircle,
  CheckCircle2,
  Activity,
  Target,
  Filter,
  FileText,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RiskDashboard = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('baseline');
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  
  const gaugeRef = useRef(null);
  const domainHeatmapRef = useRef(null);
  const moduleHeatmapRef = useRef(null);
  const phaseHeatmapRef = useRef(null);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  useEffect(() => {
    if (analysis) {
      if (activeTab === 'overview') {
        renderRiskGauge();
      }
      if (activeTab === 'heatmaps') {
        renderDomainHeatmap();
        renderModuleHeatmap();
        renderPhaseHeatmap();
      }
    }
  }, [analysis, activeTab]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/risk-analysis/latest`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.log('No risk analysis yet');
    } finally {
      setLoading(false);
    }
  };

  const generateAnalysis = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/risk-analysis/generate`, {});
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error generating risk analysis');
    } finally {
      setGenerating(false);
    }
  };

  const renderRiskGauge = () => {
    if (!gaugeRef.current || !analysis) return;

    const svg = d3.select(gaugeRef.current);
    svg.selectAll('*').remove();

    const width = 300;
    const height = 200;
    const radius = 90;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${width / 2}, ${height - 20})`);

    // Background arc
    const backgroundArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    g.append('path')
      .attr('d', backgroundArc)
      .attr('fill', '#E5E7EB');

    // Risk level arcs
    const greenArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(-Math.PI / 2 + (30 / 100) * Math.PI);

    const yellowArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2 + (30 / 100) * Math.PI)
      .endAngle(-Math.PI / 2 + (50 / 100) * Math.PI);

    const redArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2 + (50 / 100) * Math.PI)
      .endAngle(Math.PI / 2);

    g.append('path').attr('d', greenArc).attr('fill', '#10B981').attr('opacity', 0.6);
    g.append('path').attr('d', yellowArc).attr('fill', '#F59E0B').attr('opacity', 0.6);
    g.append('path').attr('d', redArc).attr('fill', '#EF4444').attr('opacity', 0.6);

    // Current value arc
    const overallIndex = analysis.risk_indices.overall_index;
    const valueAngle = -Math.PI / 2 + (overallIndex / 100) * Math.PI;

    const valueArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(valueAngle);

    const color = overallIndex >= 70 ? '#EF4444' : overallIndex >= 50 ? '#F59E0B' : '#10B981';
    g.append('path')
      .attr('d', valueArc)
      .attr('fill', color);

    // Center text
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', -30)
      .style('font-size', '36px')
      .style('font-weight', 'bold')
      .style('fill', color)
      .text(overallIndex);

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 0)
      .style('font-size', '14px')
      .style('fill', '#6B7280')
      .text('Risk Index');

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 20)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', color)
      .text(analysis.overall_risk_level);
  };

  const renderDomainHeatmap = () => {
    if (!domainHeatmapRef.current || !analysis) return;

    const svg = d3.select(domainHeatmapRef.current);
    svg.selectAll('*').remove();

    const data = analysis.heatmaps.domain_vs_impact;
    if (!data || data.length === 0) return;

    const width = 600;
    const height = 300;
    const margin = { top: 30, right: 30, bottom: 80, left: 100 };

    svg.attr('width', width).attr('height', height);

    const domains = [...new Set(data.map(d => d.domain))];
    const impactLevels = ["Low", "Medium", "High", "Critical"];

    const x = d3.scaleBand()
      .domain(impactLevels)
      .range([margin.left, width - margin.right])
      .padding(0.1);

    const y = d3.scaleBand()
      .domain(domains)
      .range([margin.top, height - margin.bottom])
      .padding(0.1);

    const colorScale = d3.scaleSequential()
      .domain([0, d3.max(data, d => d.count) || 10])
      .interpolator(d3.interpolateReds);

    // Create cells
    svg.selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', d => x(d.impact_level))
      .attr('y', d => y(d.domain))
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('fill', d => colorScale(d.count))
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .attr('rx', 4);

    // Add count labels
    svg.selectAll('text.count')
      .data(data)
      .join('text')
      .attr('class', 'count')
      .attr('x', d => x(d.impact_level) + x.bandwidth() / 2)
      .attr('y', d => y(d.domain) + y.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => d.count);

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .style('font-size', '12px')
      .style('font-weight', 'bold');

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y))
      .selectAll('text')
      .style('font-size', '12px')
      .style('font-weight', 'bold');

    // Title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Domain vs Impact Level');
  };

  const renderModuleHeatmap = () => {
    if (!moduleHeatmapRef.current || !analysis) return;

    const svg = d3.select(moduleHeatmapRef.current);
    svg.selectAll('*').remove();

    const data = analysis.heatmaps.module_vs_risk;
    if (!data || data.length === 0) return;

    const width = 600;
    const height = 300;
    const margin = { top: 30, right: 30, bottom: 60, left: 60 };

    svg.attr('width', width).attr('height', height);

    const x = d3.scaleBand()
      .domain(data.map(d => d.module))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.avg_risk_score) || 20])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const colorScale = d3.scaleSequential()
      .domain([0, d3.max(data, d => d.avg_risk_score) || 20])
      .interpolator(d3.interpolateOranges);

    // Bars
    svg.selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', d => x(d.module))
      .attr('y', d => y(d.avg_risk_score))
      .attr('width', x.bandwidth())
      .attr('height', d => y(0) - y(d.avg_risk_score))
      .attr('fill', d => colorScale(d.avg_risk_score))
      .attr('rx', 4);

    // Values
    svg.selectAll('text.value')
      .data(data)
      .join('text')
      .attr('class', 'value')
      .attr('x', d => x(d.module) + x.bandwidth() / 2)
      .attr('y', d => y(d.avg_risk_score) - 5)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', 'bold')
      .text(d => d.avg_risk_score.toFixed(1));

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .style('font-size', '12px');

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y));

    // Title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Module Risk Scores');
  };

  const renderPhaseHeatmap = () => {
    if (!phaseHeatmapRef.current || !analysis) return;

    const svg = d3.select(phaseHeatmapRef.current);
    svg.selectAll('*').remove();

    const data = analysis.heatmaps.phase_vs_concentration;
    if (!data || data.length === 0) return;

    const width = 600;
    const height = 300;
    const margin = { top: 30, right: 30, bottom: 80, left: 60 };

    svg.attr('width', width).attr('height', height);

    const x = d3.scaleBand()
      .domain(data.map(d => d.phase))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.risk_count) || 10])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const colorScale = d3.scaleSequential()
      .domain([0, d3.max(data, d => d.risk_count) || 10])
      .interpolator(d3.interpolatePurples);

    // Bars
    svg.selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', d => x(d.phase))
      .attr('y', d => y(d.risk_count))
      .attr('width', x.bandwidth())
      .attr('height', d => y(0) - y(d.risk_count))
      .attr('fill', d => colorScale(d.risk_count))
      .attr('rx', 4);

    // Values
    svg.selectAll('text.value')
      .data(data)
      .join('text')
      .attr('class', 'value')
      .attr('x', d => x(d.phase) + x.bandwidth() / 2)
      .attr('y', d => y(d.risk_count) - 5)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text(d => d.risk_count);

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')
      .style('font-size', '11px');

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y));

    // Title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Risk Concentration by Phase');
  };

  const getRiskColor = (level) => {
    if (level === 'RED') return 'bg-red-100 text-red-700 border-red-300';
    if (level === 'YELLOW') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-green-100 text-green-700 border-green-300';
  };

  const getSeverityColor = (severity) => {
    if (severity === 'critical') return 'bg-red-600 text-white';
    if (severity === 'high') return 'bg-orange-500 text-white';
    if (severity === 'medium') return 'bg-yellow-500 text-white';
    return 'bg-blue-500 text-white';
  };

  const exportRiskRegister = () => {
    if (!analysis) return;

    const csv = [
      ['Risk ID', 'Title', 'Domain', 'Module', 'Phase', 'Severity', 'Likelihood', 'Impact', 'Risk Score', 'Status'].join(','),
      ...analysis.risk_register.map(r => [
        r.risk_id,
        `"${r.title}"`,
        r.domain,
        r.module,
        r.phase,
        r.severity,
        r.likelihood,
        r.impact,
        r.risk_score,
        r.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AURA_Risk_Register_${project.name.replace(/\s/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredRisks = analysis?.risk_register.filter(r => {
    if (filterDomain !== 'all' && r.domain !== filterDomain) return false;
    if (filterSeverity !== 'all' && r.severity !== filterSeverity) return false;
    return true;
  }) || [];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-red-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Risk Dashboard</h2>
            <p className="text-gray-600">Comprehensive migration risk analysis and mitigation planning</p>
          </div>
          <div className="flex items-center space-x-3">
            {analysis && (
              <button 
                onClick={exportRiskRegister}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Export CSV</span>
              </button>
            )}
            <button 
              onClick={generateAnalysis}
              disabled={generating}
              className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Run Risk Analysis</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!analysis && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <AlertTriangle className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Risk Analysis</h3>
          <p className="text-gray-600 mb-6">Generate a comprehensive risk assessment based on Phase 1 findings</p>
          <button 
            onClick={generateAnalysis}
            className="bg-red-600 text-white px-8 py-3 rounded-xl hover:bg-red-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Analysis</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.total_risks_identified}</div>
              <div className="text-red-100 text-sm">Total Risks</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <AlertCircle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.critical_risks + analysis.summary.high_risks}</div>
              <div className="text-orange-100 text-sm">Critical + High</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <Shield className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.risk_indices.overall_index}</div>
              <div className="text-purple-100 text-sm">Risk Index</div>
            </div>
            <div className={`rounded-xl p-6 shadow-lg border-2 ${getRiskColor(analysis.overall_risk_level)}`}>
              <Target className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.overall_risk_level}</div>
              <div className="text-sm opacity-80">Risk Level</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'risks', label: 'Risk Register', icon: AlertTriangle },
                { id: 'heatmaps', label: 'Heatmaps', icon: BarChart3 },
                { id: 'scenarios', label: 'Scenarios', icon: Target },
                { id: 'narratives', label: 'AI Insights', icon: FileText }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Risk Gauge */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 text-center">Overall Migration Risk Index</h3>
                    <div className="flex justify-center">
                      <svg ref={gaugeRef}></svg>
                    </div>
                  </div>

                  {/* Risk Indices by Domain */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Risk Indices by Domain</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {Object.entries(analysis.risk_indices.by_domain).map(([domain, index]) => (
                        <div key={domain} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">{domain}</span>
                            <span className="text-sm font-bold text-gray-900">{index}/100</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                index >= 70 ? 'bg-red-600' : index >= 50 ? 'bg-yellow-500' : 'bg-green-600'
                              }`}
                              style={{ width: `${index}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top 5 Risk Themes */}
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5 text-orange-600" />
                      <span>Top Risk Themes</span>
                    </h4>
                    <ul className="space-y-2">
                      {analysis.ai_narratives.top_risk_themes.map((theme, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{theme}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Risk Register Tab */}
              {activeTab === 'risks' && (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select 
                      value={filterDomain}
                      onChange={(e) => setFilterDomain(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Domains</option>
                      {['Technical', 'Functional', 'Data', 'Infrastructure', 'Integration', 'Security', 'Program'].map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select 
                      value={filterSeverity}
                      onChange={(e) => setFilterSeverity(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Severities</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <span className="text-sm text-gray-600">{filteredRisks.length} risks</span>
                  </div>

                  {/* Risk List */}
                  <div className="space-y-3">
                    {filteredRisks.map((risk) => (
                      <div key={risk.risk_id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-300 transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(risk.severity)}`}>
                                {risk.severity.toUpperCase()}
                              </span>
                              <h4 className="text-lg font-bold text-gray-900">{risk.title}</h4>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{risk.description}</p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                {risk.domain}
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {risk.module}
                              </span>
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                {risk.phase}
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                Source: {risk.source}
                              </span>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-3xl font-bold text-red-600">{risk.risk_score}</div>
                            <div className="text-xs text-gray-500">Risk Score</div>
                            <div className="mt-2 text-xs text-gray-600">
                              L:{risk.likelihood} × I:{risk.impact}
                            </div>
                          </div>
                        </div>

                        {/* Mitigation Actions */}
                        {analysis.risk_to_backlog_mapping[risk.risk_id] && (
                          <div className="mt-4 bg-green-50 border-2 border-green-200 rounded-lg p-4">
                            <h5 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              <span>Suggested Mitigation Actions</span>
                            </h5>
                            <ul className="space-y-1">
                              {analysis.risk_to_backlog_mapping[risk.risk_id].mitigation_actions.map((action, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start space-x-2">
                                  <span className="text-green-600">•</span>
                                  <span>{action}</span>
                                </li>
                              ))}
                            </ul>
                            {analysis.risk_to_backlog_mapping[risk.risk_id].linked_backlog_items.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-green-300">
                                <p className="text-xs text-gray-600 mb-1">Linked Backlog Items:</p>
                                <div className="flex flex-wrap gap-2">
                                  {analysis.risk_to_backlog_mapping[risk.risk_id].linked_backlog_items.map((item, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-white border border-green-300 text-green-700 text-xs rounded">
                                      {item.title}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Heatmaps Tab */}
              {activeTab === 'heatmaps' && (
                <div className="space-y-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <svg ref={domainHeatmapRef}></svg>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <svg ref={moduleHeatmapRef}></svg>
                    </div>
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <svg ref={phaseHeatmapRef}></svg>
                    </div>
                  </div>
                </div>
              )}

              {/* Scenarios Tab */}
              {activeTab === 'scenarios' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Scenario Risk Comparison</h4>
                    <p className="text-sm text-gray-600">
                      Compare how risk profiles change under different migration scenarios
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    {analysis.scenario_analyses.map((scenario) => (
                      <div key={scenario.scenario_id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                        <h4 className="font-bold text-gray-900 mb-4">{scenario.scenario_name}</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-gray-600">Overall Risk Index</p>
                            <p className="text-3xl font-bold text-orange-600">
                              {scenario.risk_indices.overall_index}
                            </p>
                            <p className="text-xs text-gray-500">
                              Delta: {scenario.risk_delta.overall >= 0 ? '+' : ''}{scenario.risk_delta.overall.toFixed(1)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-2">Top Domain Risks</p>
                            {Object.entries(scenario.risk_indices.by_domain)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 3)
                              .map(([domain, index]) => (
                                <div key={domain} className="flex justify-between text-xs mb-1">
                                  <span>{domain}:</span>
                                  <span className="font-bold">{index}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Narratives Tab */}
              {activeTab === 'narratives' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold flex items-center space-x-2">
                        <FileText className="w-6 h-6 text-red-600" />
                        <span>Executive Risk Summary</span>
                      </h3>
                      {analysis.ai_narratives.generated_by.includes('GPT') && (
                        <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                          <TrendingUp className="w-3 h-3" />
                          <span>{analysis.ai_narratives.generated_by}</span>
                        </span>
                      )}
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{analysis.ai_narratives.executive_summary}</p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Critical Action Items</h4>
                    <div className="space-y-3">
                      {analysis.top_risks.slice(0, 5).map((risk, idx) => (
                        <div key={risk.risk_id} className="flex items-start space-x-3 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                          <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{risk.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{risk.description}</p>
                            <div className="flex items-center space-x-2 mt-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getSeverityColor(risk.severity)}`}>
                                {risk.severity}
                              </span>
                              <span className="text-xs text-gray-500">Score: {risk.risk_score}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RiskDashboard;
